import { useCallback, useEffect, useRef, useState } from 'react';

import * as Sentry from '@sentry/react';
import { useMutation } from '@tanstack/react-query';

import {
  NEW_USER_QUIZ_API,
  type NewUserQuizPayload,
  type NewUserQuizPriority,
  type NewUserQuizStatus,
} from '@/shared/api/newUserQuiz';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import type { NewUserQuizFormState, SingleAnswerKey, SingleAnswerValue } from '../lib/types';

import { markNewUserQuizBypassed } from '../lib/bypass';
import {
  NEW_USER_QUIZ_AUTO_ADVANCE_MS,
  NEW_USER_QUIZ_INITIAL_STATE,
  NEW_USER_QUIZ_MAX_SUBMIT_ATTEMPTS,
  NEW_USER_QUIZ_TOTAL_STEPS,
} from '../lib/constants';

const toPayload = (answers: NewUserQuizFormState): NewUserQuizPayload | null => {
  const { content_team_size, localization_frequency, priorities } = answers;

  if (content_team_size === null || localization_frequency === null || priorities.length === 0) {
    return null;
  }

  return {
    content_team_size,
    localization_frequency,
    priorities,
  };
};

export const useNewUserQuizFlow = (
  userId: string,
  onBypass: () => void,
  onCompleted: (status: NewUserQuizStatus) => void,
  onDismiss: () => void,
) => {
  const [answers, setAnswers] = useState<NewUserQuizFormState>(NEW_USER_QUIZ_INITIAL_STATE);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const advanceTimerRef = useRef<number>();
  const lastTrackedPrioritiesRef = useRef<string>();
  const shownEventTrackedRef = useRef(false);

  const { isPending: isSubmitting, mutateAsync: submitQuiz } = useMutation({ mutationFn: NEW_USER_QUIZ_API.submit });

  const clearAdvanceTimer = useCallback(() => {
    if (advanceTimerRef.current !== undefined) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = undefined;
    }
  }, []);

  useEffect(() => clearAdvanceTimer, [clearAdvanceTimer]);

  useEffect(() => {
    if (!shownEventTrackedRef.current) {
      shownEventTrackedRef.current = true;
      MixpanelEvents.newUserQuizShown();
    }
  }, []);

  const selectSingleAnswer = useCallback(
    (key: SingleAnswerKey, value: SingleAnswerValue) => {
      clearAdvanceTimer();
      setHasSubmitError(false);
      MixpanelEvents.newUserQuizQuestionAnswered({ answer: value, questionId: key, step: currentStep + 1 });
      setAnswers((current) => ({ ...current, [key]: value }) as NewUserQuizFormState);
      setIsAdvancing(true);

      advanceTimerRef.current = window.setTimeout(() => {
        setCurrentStep((step) => Math.min(step + 1, NEW_USER_QUIZ_TOTAL_STEPS - 1));
        setIsAdvancing(false);
        advanceTimerRef.current = undefined;
      }, NEW_USER_QUIZ_AUTO_ADVANCE_MS);
    },
    [clearAdvanceTimer, currentStep],
  );

  const selectPriority = useCallback((value: NewUserQuizPriority) => {
    setHasSubmitError(false);
    setAnswers((current) => ({ ...current, priorities: [value] }));
  }, []);

  const goBack = useCallback(() => {
    clearAdvanceTimer();
    setHasSubmitError(false);
    setIsAdvancing(false);
    setCurrentStep((step) => Math.max(0, step - 1));
  }, [clearAdvanceTimer]);

  const submit = useCallback(() => {
    const payload = toPayload(answers);

    if (!payload) {
      return;
    }

    const attempt = submitAttempts + 1;
    const prioritiesKey = payload.priorities.join('|');

    setHasSubmitError(false);
    if (lastTrackedPrioritiesRef.current !== prioritiesKey) {
      lastTrackedPrioritiesRef.current = prioritiesKey;
      MixpanelEvents.newUserQuizQuestionAnswered({
        answer: payload.priorities,
        attempt,
        questionId: 'priorities',
        step: NEW_USER_QUIZ_TOTAL_STEPS,
      });
    }

    submitQuiz(payload)
      .then((response) => {
        onCompleted({
          booking_answers_qualified: response?.booking_answers_qualified === true,
          booking_country_allowed: response?.booking_country_allowed === true,
          completed: true,
        });
        onDismiss();
      })
      .catch((error: unknown) => {
        Sentry.captureException(error);
        MixpanelEvents.newUserQuizSubmitFailed({ attempt });
        setSubmitAttempts((attempts) => attempts + 1);
        setHasSubmitError(true);
      });
  }, [answers, onCompleted, onDismiss, submitAttempts, submitQuiz]);

  const continueAnyway = useCallback(() => {
    MixpanelEvents.newUserQuizBypassed({ attempts: submitAttempts });
    markNewUserQuizBypassed(userId);
    onBypass();
  }, [onBypass, submitAttempts, userId]);

  return {
    answers,
    canContinueAnyway: submitAttempts >= NEW_USER_QUIZ_MAX_SUBMIT_ATTEMPTS,
    continueAnyway,
    currentStep,
    goBack,
    hasSubmitError,
    isAdvancing,
    isSubmitting,
    selectPriority,
    selectSingleAnswer,
    submit,
  };
};
